import React, { useRef, useState, useMemo, useEffect } from 'react';
import { squarify } from './treemap-utils';

// commentary=blue, risk=orange, conditional=yellow — pick dominant type colour
function dominantColor(item) {
  const max = Math.max(item.commentary_count, item.risk_count, item.conditional_count);
  if (max === 0) return '#93c5fd'; // blue-300 default
  if (max === item.risk_count) return '#fb923c';       // orange-400
  if (max === item.conditional_count) return '#facc15'; // yellow-400
  return '#60a5fa';                                      // blue-400
}

export default function CommentaryTreemap({ items, onTileClick }) {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 700, h: 280 });
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: width, h: Math.max(160, height) });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!items.length || !dims.w) return [];
    const totalArea = dims.w * dims.h;
    const totalSize = items.reduce((s, r) => s + r.mention_count, 0) || 1;
    const mapped = items.map((r) => ({
      ...r,
      _area: (r.mention_count / totalSize) * totalArea,
    })).filter((r) => r._area > 0);
    const out = [];
    squarify(mapped, 0, 0, dims.w, dims.h, out);
    return out;
  }, [items, dims]);

  if (!items.length) return null;

  return (
    <div
      ref={containerRef}
      className="relative bg-gray-100 rounded-xl overflow-hidden"
      style={{ height: 280 }}
    >
      {layout.map((item) => {
        const bg = dominantColor(item);
        const tooSmall = item.w < 40 || item.h < 28;
        return (
          <div
            key={item.ticker}
            className="absolute cursor-pointer hover:brightness-110 transition-all border border-white/30"
            style={{ left: item.x, top: item.y, width: item.w, height: item.h, backgroundColor: bg }}
            onMouseEnter={(e) => setTooltip({ item, x: e.clientX, y: e.clientY })}
            onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : t)}
            onMouseLeave={() => setTooltip(null)}
            onClick={() => onTileClick?.(item.ticker)}
          >
            {!tooSmall && (
              <div className="p-1.5 h-full flex flex-col justify-between overflow-hidden">
                <span
                  className="text-white font-semibold leading-tight drop-shadow"
                  style={{ fontSize: Math.min(13, Math.max(9, item.w / 9)) }}
                >
                  {item.ticker}
                </span>
                {item.h > 44 && (
                  <span className="text-white/80 text-[9px]">{item.mention_count} mentions</span>
                )}
              </div>
            )}
          </div>
        );
      })}

      {tooltip && (
        <div
          className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none"
          style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
        >
          <div className="font-semibold mb-1">{tooltip.item.ticker}</div>
          <div className="text-gray-300 space-y-0.5">
            <div>{tooltip.item.name}</div>
            <div>{tooltip.item.mention_count} total mentions</div>
            <div className="flex gap-2 mt-1">
              {tooltip.item.commentary_count > 0 && <span className="text-blue-300">{tooltip.item.commentary_count} commentary</span>}
              {tooltip.item.risk_count > 0 && <span className="text-orange-300">{tooltip.item.risk_count} risk</span>}
              {tooltip.item.conditional_count > 0 && <span className="text-yellow-300">{tooltip.item.conditional_count} conditional</span>}
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-2 right-2 flex items-center gap-2 text-[9px] text-white/70">
        <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-sm bg-blue-400 inline-block" />Commentary</span>
        <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-sm bg-orange-400 inline-block" />Risk</span>
        <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-sm bg-yellow-400 inline-block" />Conditional</span>
      </div>
    </div>
  );
}
